import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axiosClient';
import RaiseIssueForm from '../components/RaiseIssueForm';
import { FaSignInAlt, FaFileInvoiceDollar, FaExclamationCircle, FaLifeRing } from 'react-icons/fa';

/**
 * Public "Report an Issue" page.
 * Reachable by ANYONE — logged in or not — which matters most for the
 * single biggest reason someone lands here: they can't log in.
 *
 * - Logged-in users: fetches their jobs so disputes/payment issues can be
 *   tied to a specific job, same as RaiseIssueForm's dashboard usage.
 * - Logged-out visitors: shows the "General help" flow only, with an email
 *   field so support can reply. See the backend note in RaiseIssueForm.jsx —
 *   /support-tickets/raise/ must accept unauthenticated requests for this
 *   to actually reach your team.
 *
 * Add to your router, e.g.:
 *   <Route path="/report-issue" element={<ReportIssuePage />} />
 *
 * Then link to it from your Navbar / footer / login page ("Trouble logging in?").
 */
export default function ReportIssuePage() {
  const { user } = useAuth();
  const isAuthenticated = !!user;

  const [jobs, setJobs]       = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(isAuthenticated);

  useEffect(() => {
    if (!isAuthenticated) return;
    // ADJUST ENDPOINT — this should return the current user's jobs
    // (client jobs or craftsman jobs, whichever applies to `user.role`).
    const endpoint = user.role === 'craftsman' ? '/craftsman/jobs/' : '/client/jobs/';
    api.get(endpoint)
      .then(({ data }) => setJobs(Array.isArray(data) ? data : data?.results || []))
      .catch(() => setJobs([]))
      .finally(() => setLoadingJobs(false));
  }, [isAuthenticated, user]);

  return (
    <div style={{ background: '#0a0a0a', minHeight: '100vh', fontFamily: "'Raleway',sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Raleway:wght@400;500;600;700;800&display=swap');
        .ri-wrap{max-width:640px;margin:0 auto;padding:64px 20px 80px;}
        .ri-eyebrow{display:inline-flex;align-items:center;gap:8px;background:rgba(255,215,0,.08);border:1.5px solid rgba(255,215,0,.25);border-radius:50px;padding:6px 16px;font-size:.72rem;font-weight:700;color:#FFD700;letter-spacing:.06em;text-transform:uppercase;margin-bottom:18px;}
        .ri-title{font-size:clamp(1.7rem,4vw,2.5rem);font-weight:800;color:#f5f5f5;line-height:1.2;margin:0 0 14px;}
        .ri-sub{font-size:.95rem;color:#999;line-height:1.75;margin:0 0 32px;}
        .ri-help-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(130px,1fr));gap:10px;margin-bottom:36px;}
        .ri-help-card{background:#141414;border:1px solid rgba(255,255,255,.07);border-radius:12px;padding:14px;text-align:center;}
        .ri-help-card svg{color:#FFD700;font-size:1.1rem;margin-bottom:8px;}
        .ri-help-card p{color:#ccc;font-size:.76rem;font-weight:600;margin:0;line-height:1.4;}
        .ri-login-note{background:rgba(255,215,0,.05);border:1px solid rgba(255,215,0,.18);border-radius:12px;padding:14px 16px;margin-bottom:28px;font-size:.83rem;color:#ddd;line-height:1.6;}
        .ri-login-note a{color:#FFD700;font-weight:700;text-decoration:none;}
        .ri-login-note a:hover{text-decoration:underline;}
      `}</style>

      <div className="ri-wrap">
        <span className="ri-eyebrow"><FaLifeRing /> Support</span>
        <h1 className="ri-title">
          {isAuthenticated ? 'Report an issue' : "Need help? We're here."}
        </h1>
        <p className="ri-sub">
          {isAuthenticated
            ? "Tell us what's wrong — a job dispute, a payment that doesn't match, a complaint, or anything else — and our support team will follow up."
            : "Can't log in, having trouble with your account, or something else not working? Tell us what happened below — you don't need an account to reach us."}
        </p>

        {!isAuthenticated && (
          <>
            <div className="ri-help-grid">
              <div className="ri-help-card"><FaSignInAlt /><p>Can't log in</p></div>
              <div className="ri-help-card"><FaFileInvoiceDollar /><p>Payment problem</p></div>
              <div className="ri-help-card"><FaExclamationCircle /><p>Something's broken</p></div>
            </div>
            <div className="ri-login-note">
              Forgot your password? Try <Link to="/forgot-password">resetting it</Link> first — that's often the fastest fix.
              Still stuck, or it's something else? Use the form below.
            </div>
          </>
        )}

        <RaiseIssueForm
          jobs={isAuthenticated ? jobs : []}
          isAuthenticated={isAuthenticated}
        />

        {loadingJobs && (
          <p style={{ color: '#666', fontSize: '.78rem', marginTop: 10 }}>Loading your jobs…</p>
        )}
      </div>
    </div>
  );
}
